// Pestaña «Ventas»: tickets del SKU en el periodo de la banda, ordenables por cualquier columna.
import { useMemo, useState } from 'react';
import { Button, ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMxn, mxn } from '@/lib/dineroHelpers';
import { NO_CELL_VALUE } from '@/shared/patterns/DataTable';
import { reportPeriodLabel } from '@/shared/patterns/PeriodBand';
import { useSkuSaleRows, type SkuSalesContext } from '../../api/useSkuSaleRows';
import {
    DEFAULT_SALE_SORT,
    nextSaleSort,
    skuDetailErrorMessage,
    sortSkuSales,
    storeInfo,
    type PaymentTone,
    type SaleSortColumn,
    type StoreDirectory,
} from '../../lib/inventoryDetail';
import type { InventoryRow } from '../../lib/inventoryList';
import { DETAIL_TD, DETAIL_TH, DetailCard, DetailEmpty, MonoId } from './DetailCard';

const PAYMENT_CHIP: Record<PaymentTone, string> = {
    cash: 'bg-muted text-muted-foreground',
    sk: 'bg-primary/10 text-primary',
    other: 'bg-info-soft text-info',
};

// Padding horizontal corto: las seis columnas caben en el ancho por defecto del detalle.
const TD = cn(DETAIL_TD, 'px-1');

const COLUMNS: readonly { column: SaleSortColumn; label: string; numeric?: boolean }[] = [
    { column: 'date', label: 'Fecha' },
    { column: 'store', label: 'Tienda' },
    { column: 'seller', label: 'Vendedor' },
    { column: 'customer', label: 'Cliente' },
    { column: 'payment', label: 'Forma de pago' },
    { column: 'amount', label: 'Monto', numeric: true },
];

export interface SalesTabContext extends SkuSalesContext {
    stores: StoreDirectory;
}

/** «1 ticket» o «N tickets». */
function ticketsLabel(n: number): string {
    return `${String(n)} ticket${n === 1 ? '' : 's'}`;
}

export function SalesTab({ row, ctx }: { row: InventoryRow; ctx: SalesTabContext }) {
    const { query, rows, available } = useSkuSaleRows(row, ctx, true);
    const [sort, setSort] = useState(DEFAULT_SALE_SORT);
    const sorted = useMemo(() => sortSkuSales(rows, sort), [rows, sort]);

    if (!available) {
        return (
            <DetailEmpty>
                No podemos leer las ventas sin una cadena asignada a tu usuario.
            </DetailEmpty>
        );
    }

    const storeCode = ctx.storeId ? storeInfo(ctx.stores, ctx.storeId).code : undefined;
    const periodNote = `${reportPeriodLabel(ctx.period)}${storeCode ? ` · ${storeCode}` : ''}`;

    let body;
    if (query.error) {
        body = (
            <ErrorState
                className="m-3"
                title="No se pudieron cargar las ventas del SKU"
                description={skuDetailErrorMessage(query.error)}
                onRetry={() => void query.refetch()}
            />
        );
    } else if (query.isPending) {
        body = (
            <div className="p-3">
                <Skeleton lines={4} />
            </div>
        );
    } else if (sorted.length === 0) {
        body = <DetailEmpty>Sin ventas del SKU en el periodo seleccionado.</DetailEmpty>;
    } else {
        body = (
            <>
                <div className="max-h-[588px] overflow-auto">
                    <table className="w-full border-collapse text-[11px]">
                        <thead>
                            <tr>
                                {COLUMNS.map(({ column, label, numeric }) => {
                                    const active = sort.column === column;
                                    return (
                                        <th
                                            key={column}
                                            aria-sort={
                                                active
                                                    ? sort.direction === 'asc'
                                                        ? 'ascending'
                                                        : 'descending'
                                                    : 'none'
                                            }
                                            className={cn(
                                                DETAIL_TH,
                                                'px-1',
                                                numeric && 'text-right',
                                            )}
                                        >
                                            <button
                                                type="button"
                                                className="select-none uppercase tracking-[0.04em] hover:text-foreground"
                                                onClick={() => {
                                                    setSort(nextSaleSort(sort, column));
                                                }}
                                            >
                                                {label}
                                                <span
                                                    className={cn(
                                                        'ml-0.5 text-[8px]',
                                                        active ? 'opacity-100' : 'opacity-0',
                                                    )}
                                                    aria-hidden
                                                >
                                                    {active && sort.direction === 'asc' ? '▲' : '▼'}
                                                </span>
                                            </button>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((sale) => (
                                <tr key={sale.saleId}>
                                    <td
                                        className={cn(
                                            TD,
                                            'text-[10.5px] tabular-nums text-muted-foreground',
                                        )}
                                    >
                                        {formatDateMx(sale.soldAt)}
                                    </td>
                                    <td className={TD}>
                                        {sale.storeName}
                                        <MonoId>
                                            {sale.storeId
                                                ? storeInfo(ctx.stores, sale.storeId).code
                                                : NO_CELL_VALUE}
                                        </MonoId>
                                    </td>
                                    <td className={TD}>{sale.sellerName}</td>
                                    <td className={TD}>
                                        <span className="font-medium">{sale.customerName}</span>
                                    </td>
                                    <td className={TD}>
                                        <span
                                            className={cn(
                                                'inline-flex items-center whitespace-nowrap rounded-[3px] px-1.5 py-px text-[9.5px] font-semibold',
                                                PAYMENT_CHIP[sale.payment.tone],
                                            )}
                                        >
                                            {sale.payment.label}
                                        </span>
                                    </td>
                                    <td
                                        className={cn(
                                            TD,
                                            'whitespace-nowrap text-right font-bold tabular-nums',
                                        )}
                                    >
                                        {formatMxn(mxn(sale.amountCents))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {query.hasNextPage ? (
                    <div className="flex justify-center border-t border-border/40 p-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={query.isFetchingNextPage}
                            onClick={() => void query.fetchNextPage()}
                        >
                            {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más ventas'}
                        </Button>
                    </div>
                ) : null}
            </>
        );
    }

    return (
        <DetailCard
            title={`Ventas del SKU · ${ticketsLabel(rows.length)}`}
            aside={periodNote}
            flush
        >
            {body}
        </DetailCard>
    );
}
